/**
 * Updates the recently viewed products in localStorage.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in localStorage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store (10: supports section max_products; predictive search slices its own query) */
  static #MAX_PRODUCTS = 10;

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   */
  static addProduct(productId) {
    // Normalize so list items and comparisons stay string-based across callers.
    const id = String(productId).trim();
    if (!id) return;

    let viewedProducts = this.getProducts();

    viewedProducts = viewedProducts.filter((/** @type {string} */ existing) => existing !== id);
    viewedProducts.unshift(id);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    try {
      localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
    } catch {
      // setItem can throw on quota / private browsing; recently viewed is non-critical.
    }
  }

  static clearProducts() {
    try {
      localStorage.removeItem(this.#STORAGE_KEY);
    } catch {
      // Same as addProduct: avoid throwing from optional storage cleanup.
    }
  }

  /**
   * Retrieves the list of recently viewed products from localStorage.
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    try {
      const raw = localStorage.getItem(this.#STORAGE_KEY);
      const parsed = JSON.parse(raw || '[]');
      // Malformed or non-array JSON should not break search / section rendering.
      if (!Array.isArray(parsed)) return [];
      return parsed
        .filter((/** @type {unknown} */ x) => x !== null && x !== undefined)
        .map((/** @type {unknown} */ x) => String(x).trim())
        .filter(Boolean);
    } catch {
      // Corrupt JSON or storage read errors: treat as empty list.
      return [];
    }
  }
}
